<template>
	<div class="chat-group-order-side">
		<div class="order-head">
			<span class="order-title">订单<span class="order-role">{{ ctx.isFinance ? '财务' : '业务员' }}</span></span>
			<span class="order-head-btns">
				<span class="order-refresh el-icon-refresh" title="刷新" @click="load()"></span>
				<span class="order-close el-icon-close" title="关闭" @click="handleClose"></span>
			</span>
		</div>

		<div v-if="!ctx.bound" class="order-empty">本群未绑定Gate商户</div>

		<template v-else>
		<div class="order-tabs">
			<div v-for="t in tabs" :key="t.key" class="order-tab" :class="{ active: t.key === currentTabKey }" @click="selectTab(t.key)">
				<span class="ot-title">{{ t.title }}</span>
				<span v-if="t.items.length" class="ot-count">{{ t.items.length }}</span>
			</div>
		</div>

		<el-scrollbar class="order-scroll">
			<div v-if="loading" class="order-empty">加载中...</div>
			<div v-else-if="!currentItems.length" class="order-empty">{{ emptyText }}</div>
			<div v-else class="order-list">
						<div class="order-card" :class="{ 'oc-overdue': isOverdueCard(r) }" v-for="r in currentItems" :key="r.id">
					<!-- 买家黑名单：对手方姓名命中时醒目横幅提醒（仅提示，不影响订单流转） -->
						<div v-if="isBlacklisted(r.gateItsNick)" class="oc-blacklist-banner">
							<i class="el-icon-warning"></i>
							<span>黑名单用户·谨慎接待</span>
						</div>
						<!-- 跨商户交易：此买家在之前商户交易过，提示业务员注意(带原接待明细) -->
						<div v-if="r.tradedOtherMerchant" class="oc-cross-merchant-banner">
							<i class="el-icon-warning"></i>
							<span>此客户在之前商户交易过<span v-if="r.crossInfo">：{{ r.crossInfo.count }}单(取消{{ r.crossInfo.cancelled }}) · 原接待：【{{ (r.crossInfo.salesNames || []).join('、') || '?' }}】(原商户：{{ (r.crossInfo.merchantNames || []).join('、') }})</span></span>
						</div>
					<div class="oc-row">
						<span class="oc-txid" :title="r.orderTxid">{{ r.orderTxid }}</span>
						<!-- 同群多商户时标注订单归属商户(2026-08-18) -->
						<span v-if="showMerchantTag && r.merchantName" class="oc-merchant">{{ r.merchantName }}</span>
						<span class="oc-flow" :class="'flow-' + r.flowStatus">{{ flowText(r.flowStatus) }}</span>
					</div>
					<!-- 订单操作倒计时：订单生成起20分钟操作时限，flow 0..3显示(已放行/待回款阶段不再显示)；超时后Gate自动取消 -->
					<div v-if="showCountdown(r)" class="oc-cd-banner"
						:class="{ urgent: countdownUrgent(r), expired: countdownExpired(r) }">
						<i :class="countdownExpired(r) ? 'el-icon-warning' : 'el-icon-alarm-clock'"></i>
						<span class="cd-label">{{ countdownExpired(r) ? '订单已超时' : '订单剩余时间' }}</span>
						<span class="cd-time">{{ countdownExpired(r) ? '系统将自动取消' : countdownText(r) }}</span>
					</div>
					<div class="oc-row">
						<span class="oc-amount">{{ r.amount }} {{ r.fiat || 'CNY' }}</span>
						<span v-if="r.payChannel" class="oc-chan">{{ channelText(r.payChannel) }}</span>
					</div>
					<div class="oc-row" v-if="r.cryptoAmount != null">
						<span class="oc-crypto">{{ r.cryptoAmount }} {{ r.cryptoType || 'U' }}</span>
						<span v-if="r.rate" class="oc-rate">汇率 {{ r.rate }}</span>
					</div>
					<div class="oc-row" v-if="r.flowStatus === 0 || r.flowStatus === 1">
						<span class="oc-paytag" :class="r.buyerPaid ? 'paid' : 'unpaid'">{{ r.buyerPaid ? '买家已付款' : '等待客户付款' }}</span>
						<el-button v-if="!r.buyerPaid" type="text" size="mini" class="oc-sync-btn" :loading="syncingPaidId === r.id" @click="doSyncPaid(r)">刷新付款状态</el-button>
					</div>
					<div class="oc-meta">
						<span v-if="r.gateItsNick" :class="{ 'oc-party-black': isBlacklisted(r.gateItsNick) }">对手方：{{ r.gateItsNick }}</span>
						<span v-if="ctx.isFinance && r.salespersonName">业务员：{{ r.salespersonName }}</span>
					</div>
					<!-- 客户微信：直接挂在订单上，业务员当前微信被封时照这里把老客户加回来 -->
					<div class="oc-wechat" v-if="r.customerWechatId">
						<i class="el-icon-chat-dot-round"></i>
						<span class="ocw-id">{{ r.customerWechatId }}</span>
						<span class="ocw-nick" v-if="r.customerWechatNick">({{ r.customerWechatNick }})</span>
					</div>
					<!-- 买家手机号：财务分配时填写的验证实名手机号，业务员核实买家身份用 -->
					<div class="oc-wechat" v-if="r.buyerPhone">
						<i class="el-icon-phone"></i>
						<span class="ocw-id">{{ r.buyerPhone }}</span>
					</div>
					<!-- 老客接待历史：同交易方此前被接待过，醒目提示财务分配决策 -->
					<div v-if="r.flowStatus === 0 && r.salesHistory" class="oc-reghist">
						<i class="el-icon-warning"></i>
						<span>老客户：此前由【{{ r.salesHistory.lastSalespersonName }}】接待过{{ r.salesHistory.count }}次<span v-if="r.salesHistory.lastMerchantName">（原商户：{{ r.salesHistory.lastMerchantName }}）</span></span>
					</div>

					<!-- 已取消展示 -->
					<div v-if="r.flowStatus === -1" class="oc-cancelled">
						<span class="occ-who">已被 {{ r.cancelByName || '?' }} 取消</span>
						<span class="occ-reason">原因：{{ r.cancelReason || '-' }}</span>
					</div>

					<!-- 财务动作（按单判断：分配给自己的单走业务员视角，避免兼任财务/管理员的业务员看不到已转款等按钮） -->
					<div class="oc-actions" v-if="!actionAsSales(r)">
						<!-- 客户资料入口：仅业务员确认收款后(flowStatus>=2)展示——收到钱才算真客户，付款前不建档 -->
						<el-button v-if="r.flowStatus >= 2 && r.flowStatus <= 5" type="text" size="mini" class="oc-customer-btn" @click="openCustomer(r)">
							<i class="el-icon-user"></i>{{ r.customerName || '创建客户资料' }}
						</el-button>
						<span class="oc-act-right">
							<el-button v-if="r.flowStatus === 0" type="primary" size="mini"
								@click="openAssign(r)">分配</el-button>
							<!-- 补分配: 订单已流转但从未分配过业务员(如建单出错漏分配),补指定负责人跟进 -->
							<el-button v-else-if="r.flowStatus >= 2 && !r.salespersonId" type="danger" size="mini"
								@click="openAssign(r)">补分配</el-button>
							<template v-else-if="r.flowStatus === 1">
								<!-- 转单：改派业务员，任意阶段可操作(转单后自动给客户发新业务员的支付宝信息) -->
								<el-button type="warning" size="mini" plain
									@click="openReassign(r)">转单</el-button>
								<span class="oc-wait">等待业务员操作</span>
							</template>
							<el-button v-else-if="r.flowStatus === 2" type="warning" size="mini"
								@click="doMarkReleased(r)">标记已放行</el-button>
							<el-button v-else-if="r.flowStatus === 4" type="success" size="mini"
								@click="doFinanceReceived(r)">确认收到回款</el-button>
							<span v-else-if="r.flowStatus !== -1" class="oc-wait">等待业务员操作</span>
							<!-- 财务也不提供取消：取消一律以芝麻官方为准，由系统终态同步自动关单 -->
						</span>
					</div>
					<!-- 业务员动作（分配给自己的单，或非财务身份） -->
					<div class="oc-actions" v-else>
						<!-- 客户资料入口：仅确认收款后(flowStatus>=2)展示，未收款不得建档 -->
						<el-button v-if="r.flowStatus >= 2 && r.flowStatus <= 5" type="text" size="mini" class="oc-customer-btn" @click="openCustomer(r)">
							<i class="el-icon-user"></i>{{ r.customerName || '创建客户资料' }}
						</el-button>
						<span class="oc-act-right">
							<template v-if="r.flowStatus === 1">
								<!-- 买家已付款即可确认收款；客户档案改为确认收款后再建，不再前置拦截 -->
								<el-button v-if="r.buyerPaid" type="primary" size="mini"
									@click="openConfirm(r)">确认收款</el-button>
								<span v-else class="oc-wait oc-wait-pay">等待客户付款</span>
							</template>
							<el-button v-else-if="r.flowStatus === 3" type="success" size="mini"
								@click="doTransfer(r)">已转款</el-button>
							<span v-else-if="r.flowStatus === 4" class="oc-wait">等待财务对账</span>
							<span v-else-if="r.flowStatus !== -1" class="oc-wait">等待财务操作</span>
							<!-- 不提供人工取消：芝麻官方后台取消/超时关闭后，ReceiptSyncTask 终态同步会自动关单 -->
						</span>
					</div>
						</div>
			</div>
			<div class="order-redline" v-if="ctx.isFinance">
				红线：放行须在芝麻官方后台人工完成，本系统仅记录「已放行」标记。
			</div>
		</el-scrollbar>
		</template>

		<!-- 分配弹窗 -->
		<el-dialog title="分配订单给群成员" :visible.sync="assignDialog.visible" width="360px" append-to-body>
				<div class="dlg-line"><label>订单</label><span>{{ assignDialog.receipt && assignDialog.receipt.orderTxid }}</span></div>
				<div class="dlg-line"><label>金额</label><span>{{ assignDialog.receipt && assignDialog.receipt.amount }} {{ assignDialog.receipt && (assignDialog.receipt.fiat || 'CNY') }}</span></div>
				<div class="dlg-line">
					<!-- 微信单免填(2026-08-26):微信单走二维码链路不需要手机号;支付宝单必填(2026-08-29商用定制可放开必填) -->
					<label><i v-if="!isWechatOrder(assignDialog.receipt) && configStore.features.assignPhoneRequired" class="dlg-req">*</i>买家手机号<span v-if="isWechatOrder(assignDialog.receipt) || !configStore.features.assignPhoneRequired" style="color:#909399;font-weight:normal">（可不填）</span></label>
					<el-input v-model="assignDialog.buyerPhone" size="small" maxlength="20"
						placeholder="买家验证实名的手机号" style="width:220px"></el-input>
				</div>
				<!-- 聊天记录识别出的候选手机号(2026-08-19):点击填充,可继续手改;买家刚发的号码未同步到时点"刷新"重扫 -->
				<div class="dlg-line">
					<label>识别号码</label>
					<span class="oc-phone-cands" v-if="assignDialog.phones && assignDialog.phones.length">
						<span v-for="p in assignDialog.phones" :key="p" class="oc-phone-cand"
							:class="{active: assignDialog.buyerPhone === p}" @click="assignDialog.buyerPhone = p">{{ p }}</span>
					</span>
					<span v-else style="color:#909399;font-size:12px">暂未识别到</span>
					<el-button type="text" size="mini" style="margin-left:8px;padding:0" @click="loadPhones(false)">刷新</el-button>
				</div>
				<div class="dlg-line">
					<label>业务员</label>
					<el-select v-model="assignDialog.salespersonId" placeholder="请选择本群成员" size="small" style="width:220px">
						<!-- 后端只返回接单中的业务员;标签区分接单模式,支持微信+支付宝并存(2026-08-26) -->
						<el-option v-for="m in assignDialog.members" :key="m.userId"
							:label="memberPayLabel(m)"
							:value="m.userId"></el-option>
					</el-select>
				</div>
			<div class="dlg-tip" v-if="assignDialog.suggestName" style="color:#e6a23c">
				老客户：此前由【{{ assignDialog.suggestName }}】接待过<span v-if="assignDialog.receipt && assignDialog.receipt.salesHistory">{{ assignDialog.receipt.salesHistory.count }}次</span>，已默认选中，特殊情况可手动更换
			</div>
			<div class="dlg-tip">分配后该订单将出现在业务员的订单面板，由其确认收款。</div>
			<span slot="footer">
				<el-button size="small" @click="assignDialog.visible = false">取消</el-button>
				<el-button type="primary" size="small" :loading="assignDialog.submitting" @click="doAssign()">确认分配</el-button>
			</span>
		</el-dialog>

		<!-- 转单弹窗：财务改派业务员（任意阶段可转，后端同口径放开） -->
		<el-dialog title="转单（改派业务员）" :visible.sync="reassignDialog.visible" width="360px" append-to-body>
			<div class="dlg-line"><label>订单</label><span>{{ reassignDialog.receipt && reassignDialog.receipt.orderTxid }}</span></div>
			<div class="dlg-line"><label>金额</label><span>{{ reassignDialog.receipt && reassignDialog.receipt.amount }} {{ reassignDialog.receipt && (reassignDialog.receipt.fiat || 'CNY') }}</span></div>
			<div class="dlg-line"><label>当前业务员</label><span>{{ reassignDialog.receipt && reassignDialog.receipt.salespersonName }}</span></div>
			<div class="dlg-line">
				<label>新业务员</label>
				<el-select v-model="reassignDialog.salespersonId" placeholder="请选择本群成员" size="small" style="width:220px">
					<el-option v-for="m in reassignDialog.members" :key="m.userId"
						:label="memberPayLabel(m)"
						:value="m.userId"></el-option>
				</el-select>
			</div>
			<div class="dlg-tip">任意阶段均可转单；转单后由新业务员负责跟进，系统会自动把新业务员的接单信息（支付宝提示或微信话术+二维码）发给客户，原业务员会收到改派通知。</div>
			<span slot="footer">
				<el-button size="small" @click="reassignDialog.visible = false">取消</el-button>
				<el-button type="primary" size="small" :loading="reassignDialog.submitting" @click="doReassign()">确认转单</el-button>
			</span>
		</el-dialog>

		<!-- 确认收款弹窗 -->
		<el-dialog title="确认收款" :visible.sync="confirmDialog.visible" width="340px" append-to-body>
			<div class="dlg-line"><label>订单</label><span>{{ confirmDialog.receipt && confirmDialog.receipt.orderTxid }}</span></div>
			<div class="dlg-line"><label>金额</label><span>{{ confirmDialog.receipt && confirmDialog.receipt.amount }} {{ confirmDialog.receipt && (confirmDialog.receipt.fiat || 'CNY') }}</span></div>
			<div class="dlg-line">
				<label>收款渠道</label>
				<el-radio-group v-model="confirmDialog.payChannel">
					<el-radio :label="1">微信</el-radio>
					<el-radio :label="2">支付宝</el-radio>
				</el-radio-group>
				<div class="dlg-tip" v-if="!confirmDialog.payChannel" style="color:#e6a23c">请选择收款渠道后确认</div>
			</div>
			<span slot="footer">
				<el-button size="small" @click="confirmDialog.visible = false">取消</el-button>
				<el-button type="primary" size="small" :loading="confirmDialog.submitting" @click="doConfirm()">确认收款</el-button>
			</span>
		</el-dialog>

		<!-- 客户档案弹窗 -->
		<el-dialog title="客户档案" :visible.sync="customerDialog.visible" width="360px" append-to-body>
			<div class="dlg-line"><label>对手方</label><span>{{ customerDialog.gateItsNick || '-' }}</span></div>
			<div class="dlg-line">
				<label><i class="dlg-req">*</i>客户微信号</label>
				<el-input v-model="customerDialog.wechatId" size="small" maxlength="64"
					placeholder="必填，微信号(不是昵称)" style="width:220px"></el-input>
			</div>
			<div class="dlg-line">
				<label>微信备注名</label>
				<el-input v-model="customerDialog.wechatNick" size="small" maxlength="64"
					placeholder="选填，你微信里给他设的备注" style="width:220px"></el-input>
			</div>
			<div class="dlg-line">
				<label><i class="dlg-req">*</i>客户等级</label>
				<el-select v-model="customerDialog.level" placeholder="选择等级" size="small" clearable style="width:220px">
					<el-option label="A（优质客户）" value="A"></el-option>
					<el-option label="B（中等客户）" value="B"></el-option>
					<el-option label="C（普通客户）" value="C"></el-option>
					<el-option label="D（待观察）" value="D"></el-option>
				</el-select>
			</div>
			<div class="dlg-line">
				<label>备注</label>
				<el-input v-model="customerDialog.remark" type="textarea" :rows="2" placeholder="选填" style="width:220px"></el-input>
			</div>
			<div class="dlg-tip">对手方自动作为客户姓名，同一对手方(UID)的档案全局去重合并。</div>
			<div class="dlg-tip" style="color:#e6a23c">微信号与等级必须填全，否则这单无法点「已转款」回款给财务。</div>
			<span slot="footer">
				<el-button size="small" @click="customerDialog.visible = false">取消</el-button>
				<el-button type="primary" size="small" :loading="customerDialog.submitting" @click="doSaveCustomer()">保存</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import bizApi from '../../api/bizApi.js';

const FLOW = {
	0: '待分配', 1: '待确认收款', 2: '待财务放行', 3: '已放行(待转款)', 4: '待财务确认', 5: '已完结', '-1': '已取消'
};
const CHANNEL = { 1: '微信', 2: '支付宝' };

export default {
	name: 'chatGroupOrderSide',
	props: {
		group: { type: Object, default: () => ({}) },
		orderCtx: { type: Object, default: () => ({}) }
	},
	data() {
		return {
			loading: false,
			orders: [],
			activeTabKey: null,
			nowTs: Date.now(),
			tickTimer: null,
			reloadTimer: null,
			pollTimer: null,
			// 用户手动关闭面板标记(关闭后不再自动打开,切换群时重置)
			userClosed: false,
			assignDialog: { visible: false, receipt: null, members: [], salespersonId: '', suggestName: '', buyerPhone: '', phones: [], submitting: false },
			reassignDialog: { visible: false, receipt: null, members: [], salespersonId: '', submitting: false },
			confirmDialog: { visible: false, receipt: null, payChannel: null, submitting: false },
			customerDialog: {
				visible: false, receipt: null, gateItsNick: '', level: '', remark: '',
				wechatId: '', wechatNick: '', submitting: false
			},
			// 买家黑名单姓名集合（仅提示，不影响订单流转）
			blacklistSet: {},
			// 手动同步付款状态的订单ID(防重复点击)
			syncingPaidId: null
		};
	},
	computed: {
		ctx() {
			return this.orderCtx || {};
		},
		// 同群绑定多商户时,订单卡片才需要标注归属商户(2026-08-18)
		showMerchantTag() {
			return (this.ctx.merchantIds || []).length > 1;
		},
		// 按流转状态分桶：待分配(0) / 进行中(1,2) / 回款(3,4)；已取消(-1)与已完结(5)后端不返回
		groups() {
			const pending = [], active = [], waiting = [];
			(this.orders || []).forEach((r) => {
				if (r.flowStatus === 0) {
					pending.push(r);
				} else if (r.flowStatus === 1 || r.flowStatus === 2) {
					active.push(r);
				} else if (r.flowStatus === 3 || r.flowStatus === 4) {
					waiting.push(r);
				}
			});
			// 进行中：临期/超时(有到期时间且买家未付款)的排前，超时的自然置顶
			active.sort((a, b) => this.orderSortKey(a) - this.orderSortKey(b));
			return { pending, active, waiting };
		},
		// 角色分区 Tab：财务=待分配/进行中/回款中；业务员=进行中/待回款(业务员无未分配单)
		tabs() {
			const g = this.groups;
			if (this.ctx.isFinance) {
				return [
					{ key: 'pending', title: '待分配', items: g.pending },
					{ key: 'active', title: '进行中', items: g.active },
					{ key: 'waiting', title: '回款中', items: g.waiting }
				];
			}
			return [
				{ key: 'active', title: '进行中', items: g.active },
				{ key: 'waiting', title: '待回款', items: g.waiting }
			];
		},
		// 当前选中 Tab：用户手动选中优先；否则默认首个有单的 Tab，再退化为首个 Tab
		currentTabKey() {
			const tabs = this.tabs;
			if (this.activeTabKey && tabs.some((t) => t.key === this.activeTabKey)) {
				return this.activeTabKey;
			}
			const withItems = tabs.find((t) => t.items.length);
			return (withItems || tabs[0] || {}).key;
		},
		currentTab() {
			return this.tabs.find((t) => t.key === this.currentTabKey) || { title: '', items: [] };
		},
		currentItems() {
			return this.currentTab.items || [];
		},
		emptyText() {
			return '暂无' + (this.currentTab.title || '') + '订单';
		}
	},
	methods: {
		// 按单决定操作视角：订单分配给自己 → 业务员操作(即使兼任财务/管理员)；否则财务身份走财务操作。
		// 背景：管理员兼业务员时 isFinance=true，若只按全局角色判断会永远看到"等待业务员操作"，自己的单无法点"已转款"
		actionAsSales(r) {
			const myId = this.userStore.userInfo && this.userStore.userInfo.id;
			if (r.salespersonId && myId && r.salespersonId == myId) {
				return true;
			}
			return !this.ctx.isFinance;
		},
		flowText(s) { return FLOW[s] || '未知'; },
		channelText(c) { return CHANNEL[c] || ''; },
		selectTab(key) { this.activeTabKey = key; },
		// 用户手动关闭面板：标记后通知父组件隐藏面板，切换群时重置
		handleClose() {
			this.userClosed = true;
			this.$emit('close');
		},
		// 倒计时：订单生成起20分钟操作时限，仅 flow 0..2显示(待分配/待确认/待放行)；
		// flow=3(已放行)后Gate已放币，20分钟时限不再适用，不显示倒计时
		showCountdown(r) {
			return (r.flowStatus >= 0 && r.flowStatus <= 2) && this.deadlineTs(r) != null;
		},
		// 截止时间：优先取Gate到期时间(timest_expire)，缺失则按订单创建时间+20分钟兜底
		deadlineTs(r) {
			if (r.orderExpireTime) { return new Date(r.orderExpireTime).getTime(); }
			if (r.createdTime) { return new Date(r.createdTime).getTime() + 20 * 60 * 1000; }
			return null;
		},
		remainMs(r) {
			const dl = this.deadlineTs(r);
			if (dl == null) { return null; }
			return dl - this.nowTs;
		},
		countdownText(r) {
			const ms = this.remainMs(r);
			if (ms == null || ms <= 0) { return '00:00'; }
			const total = Math.floor(ms / 1000);
			const mm = Math.floor(total / 60);
			const ss = total % 60;
			return (mm < 10 ? '0' + mm : mm) + ':' + (ss < 10 ? '0' + ss : ss);
		},
		countdownUrgent(r) {
			const ms = this.remainMs(r);
			return ms != null && ms > 0 && ms <= 5 * 60 * 1000;
		},
		countdownExpired(r) {
			const ms = this.remainMs(r);
			return ms != null && ms <= 0;
		},
		// 排序键：flow 0..2且有截止时间的按到期升序(超时=过去时间=最小故置顶)，其余排后
		orderSortKey(r) {
			const dl = this.deadlineTs(r);
			if ((r.flowStatus >= 0 && r.flowStatus <= 2) && dl != null) {
				return dl;
			}
			return Number.MAX_SAFE_INTEGER;
		},
		// 该卡是否已超时(用于置顶标红)
		isOverdueCard(r) {
			return this.showCountdown(r) && this.countdownExpired(r);
		},
		// newMessage 命中本群时去抖刷新订单列表
		onNewMessage(msg) {
			if (!msg || !this.group || !this.group.id) { return; }
			if (msg.groupId && String(msg.groupId) === String(this.group.id)) {
				// 收到"买家已付款"通知时，本地即时更新buyerPaid(不等API刷新)，让"确认收款"按钮秒出
				this.tryLocalUpdateBuyerPaid(msg);
				if (this.reloadTimer) { clearTimeout(this.reloadTimer); }
				this.reloadTimer = setTimeout(() => { this.load(true); }, 200);
			}
		},
		// 本地即时标记买家已付款：从通知消息中提取订单号，匹配本地订单并立即更新UI
		tryLocalUpdateBuyerPaid(msg) {
			if (!msg.content || msg.content.indexOf('买家已付款') < 0) { return; }
			const m = msg.content.match(/订单号：(\S+)/);
			if (!m) { return; }
			const txid = m[1].trim();
			const order = this.orders.find((r) => r.orderTxid === txid);
			if (order && !order.buyerPaid) {
				order.buyerPaid = true;
				this.$message.success('买家已付款，请尽快确认收款');
			}
		},
		load(silent) {
			if (!this.group || !this.group.id || !this.ctx.bound) {
				this.orders = [];
				return Promise.resolve();
			}
			if (!silent) { this.loading = true; }
			return bizApi.groupOrders(this.group.id, 1, 50).then((d) => {
				this.orders = (d && d.list) || [];
			}).catch(() => { }).finally(() => { this.loading = false; });
		},
		// 自适应轮询：有待付款订单时2秒轮询(极致速度)，无则10秒(省资源)
		schedulePoll() {
			const hasUnpaid = this.orders.some((r) => !r.buyerPaid && (r.flowStatus === 0 || r.flowStatus === 1));
			const interval = hasUnpaid ? 2000 : 10000;
			this.pollTimer = setTimeout(() => {
				this.load(true).finally(() => { this.schedulePoll(); });
			}, interval);
		},
		// 买家黑名单：拉取启用中的姓名，建集合供订单卡片标红比对
		loadBlacklist() {
			bizApi.blacklistNames().then((names) => {
				const set = {};
				(names || []).forEach((n) => { const k = String(n == null ? '' : n).trim(); if (k) set[k] = 1; });
				this.blacklistSet = set;
			}).catch(() => { });
		},
		isBlacklisted(name) {
			const k = String(name == null ? '' : name).trim();
			return k !== '' && this.blacklistSet[k] === 1;
		},
		// 手动同步买家付款状态：当自动轮询遗漏时，财务/业务员可手动触发只读查询Gate
		doSyncPaid(r) {
			this.syncingPaidId = r.id;
			bizApi.syncPaid(r.id).then((d) => {
				if (d && d.buyerPaid) {
					this.$message.success('买家已付款，请尽快确认收款');
				} else {
					this.$message.info('买家尚未付款，请稍后重试');
				}
				this.load(true);
			}).catch(() => { }).finally(() => { this.syncingPaidId = null; });
		},
		openAssign(r) {
			this.assignDialog.receipt = r;
			this.assignDialog.salespersonId = '';
			this.assignDialog.suggestName = '';
			this.assignDialog.buyerPhone = '';
			this.assignDialog.phones = [];
			this.assignDialog.members = [];
			this.assignDialog.visible = true;
			// 聊天记录识别的候选手机号:自动填充第一个(已落库号码排最前),多个可点选,可手改
			this.loadPhones(true);
			bizApi.membersForAssign(this.group.id).then((list) => {
				this.assignDialog.members = list || [];
				// 老客默认分配:预选上次接待该交易方的业务员(需仍在本群),特殊情况财务可手动更换
				return bizApi.suggestSales(r.id).then((sug) => {
					if (!sug || !sug.salespersonId) return;
					const hit = this.assignDialog.members.find((m) => m.userId === sug.salespersonId);
					if (hit) {
						this.assignDialog.salespersonId = sug.salespersonId;
						this.assignDialog.suggestName = hit.nickName || sug.salespersonName;
					}
				});
			}).catch(() => { });
		},
		// 候选手机号重扫: openAssign 首次自动填充,弹窗内"刷新"按钮手动重拉(买家刚发的号码同步入库后补填)
		loadPhones(autoFill) {
			if (!this.assignDialog.receipt) return;
			bizApi.receiptPhones(this.assignDialog.receipt.id).then((phones) => {
				this.assignDialog.phones = phones || [];
				if (autoFill && this.assignDialog.phones.length && !this.assignDialog.buyerPhone) {
					this.assignDialog.buyerPhone = this.assignDialog.phones[0];
				}
			}).catch(() => { });
		},
		// 接单模式标签:微信+支付宝可同时开启(2026-08-26),兼容旧字段 payMode
		memberPayLabel(m) {
			const ali = m.alipayOk === true || m.payMode === 'alipay';
			const wx = m.wechatOk === true || m.payMode === 'wechat';
			if (ali && wx) return m.nickName + '（支付宝+微信）';
			if (wx) return m.nickName + '（微信接单）';
			return m.nickName + '（支付宝接单）';
		},
		// 微信支付订单判定:payChannel=1 为微信单,分配时免填买家手机号
		isWechatOrder(r) {
			return r && r.payChannel === 1;
		},
		doAssign() {
			if (this.configStore.features.assignPhoneRequired && !this.isWechatOrder(this.assignDialog.receipt) && (!this.assignDialog.buyerPhone || !this.assignDialog.buyerPhone.trim())) {
				this.$message.warning('请填写买家验证实名的手机号');
				return;
			}
			if (!this.assignDialog.salespersonId) {
				this.$message.warning('请选择业务员');
				return;
			}
			this.assignDialog.submitting = true;
			bizApi.assign(this.assignDialog.receipt.id, this.assignDialog.salespersonId, null, this.group.id, (this.assignDialog.buyerPhone || '').trim()).then(() => {
				this.$message.success('已分配');
				this.assignDialog.visible = false;
				this.load();
			}).catch(() => { }).finally(() => { this.assignDialog.submitting = false; });
		},
		// 财务转单：改派业务员（任意阶段可转，转单后自动给客户发新业务员的接单信息）
		openReassign(r) {
			this.reassignDialog.receipt = r;
			this.reassignDialog.salespersonId = '';
			this.reassignDialog.members = [];
			this.reassignDialog.visible = true;
			bizApi.membersForAssign(this.group.id).then((list) => {
				// 排除当前业务员，避免“转给自己”
				this.reassignDialog.members = (list || []).filter((m) => m.userId !== r.salespersonId);
			}).catch(() => { });
		},
		doReassign() {
			if (!this.reassignDialog.salespersonId) {
				this.$message.warning('请选择新业务员');
				return;
			}
			this.reassignDialog.submitting = true;
			bizApi.reassign(this.reassignDialog.receipt.id, this.reassignDialog.salespersonId, this.group.id).then(() => {
				this.$message.success('已转单');
				this.reassignDialog.visible = false;
				this.load();
			}).catch(() => { }).finally(() => { this.reassignDialog.submitting = false; });
		},
		openConfirm(r) {
			this.confirmDialog.receipt = r;
			// 登记时已判定收款方式的订单默认选中,业务员可改(手动选择仍是最终口径)
			this.confirmDialog.payChannel = (r.payChannel === 1 || r.payChannel === 2) ? r.payChannel : null;
			this.confirmDialog.visible = true;
		},
		doConfirm() {
			if (!this.confirmDialog.payChannel) {
				this.$message.warning('请选择收款渠道（微信或支付宝）');
				return;
			}
			this.confirmDialog.submitting = true;
			bizApi.confirm(this.confirmDialog.receipt.id, this.confirmDialog.payChannel).then(() => {
				this.$message.success('已确认收款，请创建客户资料，待财务放行');
				this.confirmDialog.visible = false;
				this.load();
			}).catch(() => { }).finally(() => { this.confirmDialog.submitting = false; });
		},
		doMarkReleased(r) {
			this.$confirm('确认此订单已在【芝麻官方后台】人工放行？本系统仅记录标记，不会调用任何 Gate 接口。', '标记已放行', {
				confirmButtonText: '确认已放行', cancelButtonText: '取消', type: 'warning'
			}).then(() => {
				return bizApi.markReleased(r.id);
			}).then(() => {
				this.$message.success('已标记放行');
				this.load();
			}).catch(() => { });
		},
		doTransfer(r) {
			// 回款前置拦截：客户资料未完善的直接开档案弹窗，不白跑一轮后端报错。
			// 后端 /receipt/transfer 同样会硬校验，这里只是把提示提前到点击即时。
			if (!r.customerWechatId || !r.customerLevel) {
				const miss = !r.customerWechatId && !r.customerLevel ? '客户微信号和等级'
					: (!r.customerWechatId ? '客户微信号' : '客户等级');
				this.$message.warning('回款前请先补全' + miss);
				this.openCustomer(r);
				return;
			}
			this.$confirm('确认已将此订单款项转给财务？', '已转款', {
				confirmButtonText: '确认已转款', cancelButtonText: '取消', type: 'warning'
			}).then(() => {
				return bizApi.transfer(r.id);
			}).then(() => {
				this.$message.success('已标记转款');
				this.load();
			}).catch(() => { });
		},
		doFinanceReceived(r) {
			this.$confirm('确认已收到业务员转来的款项？确认后该订单完结。', '确认收到回款', {
				confirmButtonText: '确认收到', cancelButtonText: '取消', type: 'success'
			}).then(() => {
				return bizApi.financeReceived(r.id);
			}).then(() => {
				this.$message.success('订单已完结');
				this.load();
			}).catch(() => { });
		},
		openCustomer(r) {
			this.customerDialog.receipt = r;
			this.customerDialog.gateItsNick = r.gateItsNick || '';
			this.customerDialog.level = '';
			this.customerDialog.remark = '';
			this.customerDialog.wechatId = '';
			this.customerDialog.wechatNick = '';
			this.customerDialog.visible = true;
			bizApi.customerByReceipt(r.id).then((d) => {
				if (d) {
					this.customerDialog.gateItsNick = d.gateItsNick || this.customerDialog.gateItsNick;
					this.customerDialog.level = d.level || '';
					this.customerDialog.remark = d.remark || '';
					this.customerDialog.wechatId = d.wechatId || '';
					this.customerDialog.wechatNick = d.wechatNick || '';
				}
			}).catch(() => { });
		},
		doSaveCustomer() {
			const wechatId = (this.customerDialog.wechatId || '').trim();
			if (!wechatId) {
				this.$message.warning('请填写客户微信号');
				return;
			}
			this.customerDialog.submitting = true;
			bizApi.customerSave(this.customerDialog.receipt.id,
				this.customerDialog.level || null, this.customerDialog.remark,
				wechatId, (this.customerDialog.wechatNick || '').trim()).then(() => {
					this.$message.success('客户档案已保存');
					this.customerDialog.visible = false;
					this.load();
				}).catch(() => { }).finally(() => { this.customerDialog.submitting = false; });
		}
	},
	watch: {
		'group.id'() { this.userClosed = false; this.activeTabKey = null; this.load(); },
		'orderCtx.bound'() { if (!this.userClosed) { this.load(); } }
	},
	mounted() {
		this.load();
		this.loadBlacklist();
		// 1s 刷新倒计时基准时间
		this.tickTimer = setInterval(() => { this.nowTs = Date.now(); }, 1000);
		// 监听新消息，命中本群时刷新(订单分配/取消等通知到达即刷新)
		if (this.$eventBus) {
			this.$eventBus.$on('newMessage', this.onNewMessage);
		}
		// 自适应轮询兜底：有待付款订单时2秒轮询(极致速度)，无则10秒(省资源)
		this.schedulePoll();
	},
	beforeDestroy() {
		if (this.tickTimer) { clearInterval(this.tickTimer); this.tickTimer = null; }
		if (this.reloadTimer) { clearTimeout(this.reloadTimer); this.reloadTimer = null; }
		if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
		if (this.$eventBus) {
			this.$eventBus.$off('newMessage', this.onNewMessage);
		}
	}
}
</script>

<style scoped lang="scss">
.chat-group-order-side {
	height: 100%;
	display: flex;
	flex-direction: column;
	background: var(--im-bg-color, #fff);
}

.order-head {
	flex-shrink: 0;
	height: 44px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 16px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);

	.order-title {
		font-weight: 600;
		font-size: 15px;
	}

	.order-role {
		margin-left: 6px;
		font-size: 12px;
		color: #fff;
		background: var(--im-color-primary, #409eff);
		border-radius: 8px;
		padding: 1px 8px;
	}

	.order-refresh {
		cursor: pointer;
		color: var(--im-text-color-light, #909399);
		font-size: 16px;

		&:hover {
			color: var(--im-color-primary, #409eff);
		}
	}

	.order-head-btns {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.order-close {
		cursor: pointer;
		color: var(--im-text-color-light, #909399);
		font-size: 16px;

		&:hover {
			color: #f56c6c;
		}
	}
}

.order-tabs {
	flex-shrink: 0;
	display: flex;
	align-items: stretch;
	padding: 0 8px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.order-tab {
	position: relative;
	display: flex;
	align-items: center;
	gap: 5px;
	padding: 10px 12px;
	cursor: pointer;
	font-size: 13px;
	color: var(--im-text-color-light, #909399);
	user-select: none;

	.ot-count {
		font-size: 11px;
		font-weight: 600;
		color: #fff;
		background: var(--im-text-color-lighter, #c0c4cc);
		border-radius: 8px;
		padding: 0 6px;
		line-height: 16px;
	}

	&:hover {
		color: var(--im-color-primary, #409eff);
	}

	&.active {
		color: var(--im-color-primary, #409eff);
		font-weight: 600;

		&::after {
			content: '';
			position: absolute;
			left: 12px;
			right: 12px;
			bottom: -1px;
			height: 2px;
			background: var(--im-color-primary, #409eff);
			border-radius: 2px;
		}

		.ot-count {
			background: var(--im-color-primary, #409eff);
		}
	}
}

.order-scroll {
	flex: 1;
	overflow: hidden;
}

.order-empty {
	padding: 40px 16px;
	text-align: center;
	color: var(--im-text-color-lighter, #c0c4cc);
	font-size: 13px;
}

.order-list {
	padding: 10px 12px;
}

.oc-section {
	margin-bottom: 6px;
}

.oc-section-title {
	display: flex;
	align-items: center;
	font-size: 12px;
	font-weight: 600;
	color: var(--im-text-color-light, #909399);
	margin: 2px 2px 8px;

	.oc-section-count {
		margin-left: 6px;
		font-size: 11px;
		font-weight: 500;
		color: #fff;
		background: var(--im-color-primary, #409eff);
		border-radius: 8px;
		padding: 0 6px;
	}

	&.is-cancel {
		color: #f56c6c;

		.oc-section-count {
			background: #f56c6c;
		}
	}
}

.order-card {
	background: var(--im-bg-color-light, #f7f8fa);
	border: 1px solid rgba(0, 0, 0, 0.05);
	border-radius: 10px;
	padding: 10px 12px;
	margin-bottom: 10px;

	&.oc-overdue {
		border-color: #f56c6c;
		background: rgba(245, 108, 108, 0.06);
		box-shadow: 0 0 0 1px rgba(245, 108, 108, 0.35);
	}

	.oc-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 4px;
	}

	.oc-txid {
		font-size: 12px;
		color: var(--im-text-color-light, #909399);
		max-width: 130px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	/* 同群多商户时标注订单归属商户 */
	.oc-merchant {
		font-size: 11px;
		color: #1677ff;
		background: rgba(22, 119, 255, 0.08);
		border-radius: 4px;
		padding: 1px 6px;
		margin: 0 4px;
		flex-shrink: 0;
	}

	.oc-amount {
		font-size: 16px;
		font-weight: 700;
		color: var(--im-text-color, #303133);
	}

	.oc-chan {
		font-size: 12px;
		color: #67c23a;
	}

	.oc-crypto {
		font-size: 13px;
		font-weight: 600;
		color: #409eff;
	}

	.oc-rate {
		font-size: 12px;
		color: var(--im-text-color-light, #909399);
	}

	.oc-paytag {
		font-size: 11px;
		border-radius: 6px;
		padding: 1px 8px;

		&.paid { background: rgba(103, 194, 58, 0.15); color: #67c23a; }
		&.unpaid { background: rgba(230, 162, 60, 0.15); color: #e6a23c; }
	}

	.oc-countdown {
		font-size: 12px;
		font-weight: 600;
		color: var(--im-text-color-light, #909399);
		display: inline-flex;
		align-items: center;

		i { margin-right: 3px; }

		&.urgent {
			color: #f56c6c;
		}

		&.expired {
			color: #f56c6c;
			font-weight: 700;
		}
	}

	.oc-cd-banner {
		display: flex;
		align-items: center;
		gap: 6px;
		margin: 2px 0 8px;
		padding: 8px 10px;
		border-radius: 8px;
		background: rgba(64, 158, 255, 0.12);
		color: #409eff;

		i {
			font-size: 18px;
		}

		.cd-label {
			font-size: 13px;
			font-weight: 600;
		}

		.cd-time {
			margin-left: auto;
			font-size: 20px;
			font-weight: 800;
			letter-spacing: 1px;
			font-variant-numeric: tabular-nums;
		}

		&.urgent {
			background: rgba(245, 108, 108, 0.15);
			color: #f56c6c;
			animation: ocCdPulse 1s ease-in-out infinite;
		}

		&.expired {
			background: #f56c6c;
			color: #fff;

			.cd-time {
				font-size: 15px;
			}
		}
	}

	@keyframes ocCdPulse {

		0%,
		100% {
			opacity: 1;
		}

		50% {
			opacity: 0.55;
		}
	}

	.oc-blacklist-banner {
			display: flex;
			align-items: center;
			gap: 6px;
			margin: 2px 0 8px;
			padding: 8px 10px;
			border-radius: 8px;
			background: #f56c6c;
			color: #fff;
			font-size: 13px;
			font-weight: 700;
			animation: ocCdPulse 1.2s ease-in-out infinite;

			i {
				font-size: 18px;
			}
		}

		.oc-cross-merchant-banner {
			display: flex;
			align-items: center;
			gap: 6px;
			margin: 2px 0 8px;
			padding: 6px 10px;
			border-radius: 8px;
			background: rgba(144, 147, 153, 0.15);
			color: #606266;
			font-size: 12px;
			font-weight: 600;

			i {
				font-size: 14px;
				color: #909399;
			}
		}

	.oc-party-black {
		color: #f56c6c;
		font-weight: 700;
	}

	.oc-cancelled {
		display: flex;
		flex-direction: column;
		gap: 2px;
		margin-bottom: 6px;
		padding: 6px 8px;
		font-size: 12px;
		line-height: 1.5;
		color: #f56c6c;
		background: rgba(245, 108, 108, 0.08);
		border-radius: 6px;

		.occ-who { font-weight: 600; }
	}

	.oc-flow {
		font-size: 11px;
		border-radius: 6px;
		padding: 1px 8px;
		background: rgba(64, 158, 255, 0.12);
		color: #409eff;

		&.flow-0 { background: rgba(144, 147, 153, 0.15); color: #909399; }
		&.flow-2 { background: rgba(230, 162, 60, 0.15); color: #e6a23c; }
		&.flow-3 { background: rgba(64, 158, 255, 0.15); color: #409eff; }
		&.flow-4 { background: rgba(230, 162, 60, 0.15); color: #e6a23c; }
		&.flow-5 { background: rgba(103, 194, 58, 0.15); color: #67c23a; }
		&.flow--1 { background: rgba(245, 108, 108, 0.15); color: #f56c6c; }
	}

	.oc-meta {
		font-size: 12px;
		color: var(--im-text-color-light, #909399);
		display: flex;
		flex-direction: column;
		gap: 2px;
		margin-bottom: 6px;
	}

	/* 客户微信：绿色小标，与订单其它 meta 区分，方便一眼扫到联系方式 */
	.oc-wechat {
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 12px;
		color: #07c160;
		margin-bottom: 6px;
		word-break: break-all;

		.ocw-id {
			font-weight: 600;
		}

		.ocw-nick {
			color: var(--im-text-color-light, #909399);
		}
	}

	/* 老客接待历史横幅：橙色醒目提示，与 element-ui warning 色系一致 */
	.oc-reghist {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-bottom: 6px;
		padding: 5px 8px;
		font-size: 12px;
		line-height: 1.5;
		color: #e6a23c;
		font-weight: 600;
		background: #fdf6ec;
		border: 1px solid #f5dab1;
		border-radius: 6px;

		i { font-size: 14px; }
	}

	.oc-actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.oc-act-right {
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.oc-customer-btn {
		padding: 0;
		font-size: 12px;

		i { margin-right: 3px; }
	}

	.oc-wait {
		font-size: 12px;
		color: var(--im-text-color-lighter, #c0c4cc);
	}

	.oc-wait-pay {
		color: #e6a23c;
	}

	.oc-sync-btn {
		font-size: 11px;
		padding: 0 4px;
		color: var(--im-color-primary, #409eff);
	}
}

.order-redline {
	margin: 4px 12px 14px;
	padding: 8px 10px;
	font-size: 11px;
	line-height: 1.5;
	color: #e6a23c;
	background: rgba(230, 162, 60, 0.08);
	border-radius: 8px;
}

.dlg-line {
	display: flex;
	align-items: center;
	margin-bottom: 12px;

	label {
		width: 76px;
		flex-shrink: 0;
		color: var(--im-text-color-light, #909399);
		font-size: 13px;
	}

	/* 必填星号：客户微信号/等级是回款前置条件，标出来省一次报错 */
	.dlg-req {
		color: #f56c6c;
		font-style: normal;
		margin-right: 2px;
	}
}

.dlg-tip {
	font-size: 12px;
	color: var(--im-text-color-lighter, #c0c4cc);
	margin-top: 4px;
}

/* 聊天记录识别的候选手机号(2026-08-19):点选填充,选中态高亮 */
.oc-phone-cands {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;

	.oc-phone-cand {
		padding: 2px 10px;
		border-radius: 12px;
		background: #ecf5ff;
		color: #409eff;
		font-size: 12px;
		cursor: pointer;
		border: 1px solid transparent;

		&:hover {
			border-color: #409eff;
		}

		&.active {
			background: #409eff;
			color: #fff;
		}
	}
}
</style>
